import { Button, RowInput } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { type PropsWithChildren, type ReactNode, useCallback } from 'react';

import * as styles from './selector-layout.css';

export interface SelectorContentProps extends PropsWithChildren {
  searchPlaceholder?: string;
  selectedCount?: number;

  onSearch?: (value: string) => void;
  onClear?: () => void;
  onCancel?: () => void;
  onConfirm?: () => void;

  actions?: ReactNode;
}

/**
 * Provides a unified layout for doc/collection/tag selector
 * - Header (Search input)
 * - Content
 * - Footer (Selected count + Actions)
 */
export const SelectorLayout = ({
  children,
  searchPlaceholder,
  selectedCount,

  onSearch,
  onClear,
  onCancel,
  onConfirm,

  actions,
}: SelectorContentProps) => {
  const t = useI18n();

  const onSearchChange = useCallback(
    (value: string) => {
      onSearch?.(value);
    },
    [onSearch]
  );

  return (
    <div className={styles.root} data-testid="doc-selector-layout">
      <header className={styles.header}>
        <RowInput
          data-testid="doc-selector-search-input"
          className={styles.search}
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
          debounce={200}
        />
      </header>

      <main className={styles.content}>{children}</main>

      <footer className={styles.footer}>
        <div className={styles.footerInfo}>
          <div className={styles.selectedCount}>
            <span>{t['com.streamlain.selectPage.selected']()}</span>
            <span className={styles.selectedNum}>{selectedCount ?? 0}</span>
          </div>
          <Button
            variant="plain"
            className={styles.clearButton}
            onClick={onClear}
          >
            {t['com.streamlain.editCollection.pages.clear']()}
          </Button>
        </div>

        <div className={styles.footerAction}>
          {actions ?? (
            <>
              <Button
                data-testid="doc-selector-cancel-button"
                onClick={onCancel}
                className={styles.actionButton}
              >
                {t['Cancel']()}
              </Button>
              <Button
                data-testid="doc-selector-confirm-button"
                onClick={onConfirm}
                className={styles.actionButton}
                variant="primary"
              >
                {t['Confirm']()}
              </Button>
            </>
          )}
        </div>
      </footer>
    </div>
  );
};
